/**
 * List of individual audit checks with a pass/warn/info/fail badge, shared by
 * the English and Bengali free audit pages.
 */
import { Check } from "lucide-react";
import type { AuditCheck, CheckStatus } from "@/lib/site-audit";

const BADGE: Record<CheckStatus, string> = {
  pass: "bg-primary/15 text-primary",
  warn: "bg-yellow-500/15 text-yellow-400",
  info: "bg-muted text-muted-foreground",
  fail: "bg-destructive/15 text-destructive",
};

export function AuditCheckList({
  checks,
  className,
  statusLabel = (s) => s,
  warnAsFail = false,
}: {
  checks: readonly AuditCheck[];
  className: string;
  /** Screen-reader label for a status. */
  statusLabel?: (status: CheckStatus) => string;
  /** Show warnings with the failure colour (Bengali page). */
  warnAsFail?: boolean;
}) {
  return (
    <ul className={className}>
      {checks.map((c) => (
        <li key={c.label} className="py-4 flex items-start gap-4">
          <span
            className={`mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full text-xs font-bold ${
              BADGE[warnAsFail && c.status === "warn" ? "fail" : c.status]
            }`}
            aria-hidden
          >
            {c.status === "pass" ? (
              <Check size={14} />
            ) : c.status === "warn" ? (
              "!"
            ) : c.status === "info" ? (
              "i"
            ) : (
              "×"
            )}
          </span>
          <div className="min-w-0">
            <div className="font-medium">
              {c.label}
              <span className="sr-only"> — {statusLabel(c.status)}</span>
            </div>
            <div className="text-sm text-foreground/90 mt-0.5 break-words">{c.found}</div>
            <div className="text-sm text-muted-foreground mt-0.5">{c.note}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}
